/**
 * One engagement agreement (S-A32).
 *
 * ===========================================================================
 * THE BODY IS EVIDENCE. IT IS SHOWN WHOLE.
 * ===========================================================================
 * `rendered_body` is frozen when the agreement is issued. A client agreed to those
 * exact bytes, and `document_checksum` is taken over them, so a dispute is settled by
 * comparison rather than by assertion.
 *
 * That is why there is no "read more", no clamped height, no summary and no ellipsis
 * anywhere near it. A collapsed contract is a contract somebody skimmed. It is also
 * why the template edited next week cannot appear here: the agreement stopped
 * referring to the template the moment it was rendered, and the panel says so, because
 * a reader who does not know that will assume the opposite.
 * ===========================================================================
 *
 * THREE THINGS THIS SCREEN HAS TO KEEP APART, and the layout follows them:
 *
 *   THE TEXT           what was agreed
 *   THE RECIPIENTS     who has acted and who the firm should be chasing — "sent" on
 *                      its own is not an answer when there are joint signatories
 *   THE EVIDENCE       when, by what method, from what address, over what checksum
 *
 * The counterparty name is fetched from the one client or lead record this agreement
 * points at. The list screen deliberately does not do that; see its header for why.
 */

import {
  STAFF,
  qk,
  useApiQuery,
  type Agreement,
  type AgreementRecipient,
  type AgreementStatusReport,
  type Client,
  type Lead,
  type SignatureEvidence,
} from '@legalcrm/api';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  ErrorState,
  Skeleton,
  StatusPill,
  Table,
  type Column,
} from '@legalcrm/ui';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { Can } from '@/auth/permissions';
import { formatDateTime, humanise } from '@/lib/format';
import { PageHeader } from '@/shell/PageHeader';

import { AddRecipientDialog } from './AddRecipientDialog';
import {
  CountersignDialog,
  RecordExecutionDialog,
  ResendLinkButton,
  SendAgreementDialog,
} from './AgreementActions';
import { agreementTone } from './AgreementListPage';
import { WithdrawAgreementDialog, canWithdraw } from './WithdrawAgreementDialog';

type OpenDialog = 'send' | 'countersign' | 'execution' | 'withdraw' | null;

export function AgreementDetailPage() {
  const { agreementId = '' } = useParams();
  const [dialog, setDialog] = useState<OpenDialog>(null);
  const navigate = useNavigate();

  const query = useApiQuery<Agreement>(
    qk.detail('agreements', agreementId),
    STAFF.agreement(agreementId),
    { enabled: Boolean(agreementId) },
  );

  /**
   * The status payload is a second call rather than a field on the detail, because it
   * is where `awaiting` and `document_checksum` live — the two things the detail
   * serializer does not carry and that this screen exists to answer.
   */
  const status = useApiQuery<AgreementStatusReport>(
    qk.sub('agreements', agreementId, 'status'),
    STAFF.agreementStatus(agreementId),
    { enabled: Boolean(agreementId) },
  );

  /** A bare array, not a cursor page — a small, complete record. */
  const evidence = useApiQuery<SignatureEvidence[]>(
    qk.sub('agreements', agreementId, 'evidence'),
    STAFF.agreementEvidence(agreementId),
    { enabled: Boolean(agreementId) },
  );

  if (query.isLoading) {
    return (
      <div className="flex flex-col gap-4" aria-busy="true">
        <span className="sr-only">Loading agreement</span>
        <Skeleton className="h-8 w-1/2" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  // `ErrorState` decides the wording. A 404 here means "absent as far as you are
  // concerned" and must not be softened into a permissions hint.
  if (query.error || !query.data) {
    return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;
  }

  const agreement = query.data;
  const awaiting = status.data?.awaiting ?? [];
  const isDraft = agreement.status === 'draft';
  const isActive = agreement.status === 'active';
  // Withdrawn: nothing further can be signed, recorded or countersigned on it.
  const isWithdrawn = agreement.status === 'cancelled';
  const canRecordExecution = agreement.recipients.some(
    (recipient) => recipient.status !== 'accepted',
  );

  return (
    <>
      <PageHeader
        title={agreement.title}
        meta={
          <>
            <StatusPill label={humanise(agreement.status)} tone={agreementTone(agreement.status)} />
            <Badge tone="neutral" size="sm">
              Version {agreement.version_number}
            </Badge>
            <Counterparty agreement={agreement} />
            {agreement.superseded_by && (
              <Link
                to={`/agreements/${agreement.superseded_by}`}
                className="text-sm text-primary hover:underline"
              >
                Superseded — open the replacement
              </Link>
            )}
          </>
        }
        actions={
          /*
           * `agreement.edit` is the capability behind all three: the catalogue grants
           * no `agreement.approve` at any scope, and the viewset maps send, execution
           * and countersign onto `edit`. `_actions` is the accurate answer for THIS
           * record, so it wins over a bare capability check.
           */
          <>
            {/* `delete` is its own grant (administrators, and partners once the firm has
                departments), so it sits outside the `edit` block below. */}
            {canWithdraw(agreement.status) && (
              <Can actions={agreement._actions} action="delete">
                <Button variant="ghost" onClick={() => setDialog('withdraw')}>
                  {isDraft ? 'Delete draft' : 'Withdraw'}
                </Button>
              </Can>
            )}
            <Can actions={agreement._actions} action="edit">
              {isDraft && (
                <Button variant="primary" onClick={() => setDialog('send')}>
                  Issue for signature
                </Button>
              )}
              {!isDraft && !isWithdrawn && canRecordExecution && (
                <Button variant="secondary" onClick={() => setDialog('execution')}>
                  Record a signature
                </Button>
              )}
              {!isDraft && !isWithdrawn && !isActive && (
                <Button variant="primary" onClick={() => setDialog('countersign')}>
                  Countersign
                </Button>
              )}
            </Can>
          </>
        }
      />

      {isDraft && (
        <NothingSentYet
          recipients={agreement.recipients}
          issueButton={
            <Can actions={agreement._actions} action="edit">
              <Button variant="primary" size="sm" onClick={() => setDialog('send')}>
                Issue for signature
              </Button>
            </Can>
          }
        />
      )}

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="flex flex-col gap-4 lg:col-span-2">
          <AgreementBody agreement={agreement} />
          <RecipientsPanel
            agreement={agreement}
            recipients={agreement.recipients}
            awaiting={awaiting}
          />
        </div>

        <div className="flex flex-col gap-4">
          <ExecutionRecord agreement={agreement} status={status.data} />
          <EvidencePanel
            evidence={evidence.data}
            loading={evidence.isLoading}
            error={evidence.error}
            onRetry={() => void evidence.refetch()}
            recipients={agreement.recipients}
          />
        </div>
      </div>

      {dialog === 'send' && (
        <SendAgreementDialog agreement={agreement} onClose={() => setDialog(null)} />
      )}
      {dialog === 'countersign' && (
        <CountersignDialog
          agreement={agreement}
          outstanding={awaiting}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog === 'execution' && (
        <RecordExecutionDialog agreement={agreement} onClose={() => setDialog(null)} />
      )}
      {dialog === 'withdraw' && (
        <WithdrawAgreementDialog
          agreement={agreement}
          onClose={() => setDialog(null)}
          // A deleted draft no longer exists, so there is nothing left on this page to
          // show. Back to where the reader came from; a withdrawn letter stays open.
          onDone={isDraft ? () => navigate(-1) : undefined}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Nothing has been sent
// ---------------------------------------------------------------------------

/**
 * The notice that ends the confusion this whole job came from.
 *
 * A signatory was added, "Issue for signature" was never pressed, and the lead heard
 * nothing. Everybody involved read "draft" as "waiting on them" — which is a perfectly
 * reasonable reading of a screen that shows a document, a named signatory and a status
 * word, and says nothing about whether anything left the building.
 *
 * So it is said in a sentence, at the top, before the letter: nothing has been sent, and
 * here is the button that sends it. Not a badge and not a tone — a badge is what "draft"
 * already was.
 */
export function NothingSentYet({
  recipients,
  issueButton,
}: {
  recipients: AgreementRecipient[];
  /**
   * Passed in rather than rendered here, so this component stays a pure statement of
   * fact. The capability gate belongs to the screen that owns the record's `_actions`.
   */
  issueButton?: React.ReactNode;
}) {
  const required = recipients.filter((recipient) => recipient.is_required);

  return (
    <div className="mb-4 rounded border border-warning/40 bg-warning-subtle px-4 py-3">
      <p className="text-base font-semibold text-ink">Nothing has been sent yet.</p>
      <p className="mt-1 text-sm text-ink-muted">
        {required.length === 0
          ? 'This is a draft. Add at least one required signatory, then issue it — adding somebody does not send them anything on its own.'
          : `This is a draft. Adding a signatory does not send them anything: ${
              required.length === 1
                ? 'the one required signatory below gets their link'
                : `each of the ${required.length} required signatories below gets their own link`
            } only when you issue it.`}
      </p>
      {required.length > 0 && issueButton && <div className="mt-2">{issueButton}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// The text
// ---------------------------------------------------------------------------

function AgreementBody({ agreement }: { agreement: Agreement }) {
  const frozen = Boolean(agreement.sent_at);

  return (
    <Card>
      <CardHeader
        title="The agreement, as it stands"
        description={
          frozen
            ? 'Frozen when this was issued. Editing the template will not change these words.'
            : 'Still a draft. Issuing it will freeze this text as it reads now.'
        }
      />
      <CardBody>
        {agreement.rendered_body ? (
          /*
           * No line clamp, no max height, no "show more". Whole, always. See the
           * module header — this is the thing somebody agreed to.
           */
          <div className="whitespace-pre-line text-base leading-relaxed text-ink">
            {agreement.rendered_body}
          </div>
        ) : (
          <p className="text-sm text-ink-subtle">
            No text has been rendered onto this agreement yet.
          </p>
        )}
      </CardBody>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Who has acted
// ---------------------------------------------------------------------------

function recipientTone(recipient: AgreementRecipient) {
  switch (recipient.status) {
    case 'accepted':
      return 'success' as const;
    case 'rejected':
      return 'danger' as const;
    case 'viewed':
      return 'info' as const;
    default:
      return 'neutral' as const;
  }
}

function RecipientsPanel({
  agreement,
  recipients,
  awaiting,
}: {
  agreement: Agreement;
  recipients: AgreementRecipient[];
  awaiting: AgreementRecipient[];
}) {
  const outstanding = new Set(awaiting.map((recipient) => recipient.id));
  const [adding, setAdding] = useState(false);
  const isDraft = agreement.status === 'draft';

  const columns: Column<AgreementRecipient>[] = [
    {
      key: 'display_name',
      header: 'Signatory',
      render: (row) => (
        <div className="min-w-0">
          <p className="font-medium text-ink">{row.display_name}</p>
          {row.email && <p className="truncate text-xs text-ink-muted">{row.email}</p>}
        </div>
      ),
    },
    {
      key: 'is_required',
      header: 'Required',
      width: 'w-32',
      render: (row) =>
        row.is_required ? (
          <Badge tone="neutral" size="sm">
            Required
          </Badge>
        ) : (
          <span className="text-sm text-ink-subtle">Optional</span>
        ),
    },
    {
      key: 'link_sent_at',
      header: 'Link sent',
      render: (row) =>
        row.link_sent_at ? (
          <span className="tabular-nums text-sm text-ink">{formatDateTime(row.link_sent_at)}</span>
        ) : isDraft ? (
          <span className="text-sm text-ink-subtle">Not yet — still a draft</span>
        ) : (
          /*
           * Issued, and still nothing reached this person. That happens when they have no
           * email address and no telephone number, or when the mint or the send failed
           * for them alone — and it is invisible from the agreement's own `sent_at`,
           * which says only that the firm issued it.
           */
          <span className="text-sm font-medium text-danger">Nothing sent to this person</span>
        ),
    },
    {
      key: 'status',
      header: 'Signed',
      render: (row) => (
        <div className="flex flex-col items-start gap-1">
          <Badge tone={recipientTone(row)} size="sm" dot>
            {row.status === 'accepted' ? 'Signed' : humanise(row.status)}
          </Badge>
          {row.acted_at ? (
            <span className="tabular-nums text-xs text-ink-muted">
              {formatDateTime(row.acted_at)}
            </span>
          ) : (
            /* Named in words, not implied by a colour: this is who to chase. */
            outstanding.has(row.id) && (
              <span className="text-xs text-warning">Still waiting on this person</span>
            )
          )}
        </div>
      ),
    },
    {
      key: 'resend',
      header: '',
      align: 'right',
      width: 'w-28',
      render: (row) =>
        // Draft: there is no link to resend, because none has ever been minted — the
        // notice above says so and offers the button that actually applies. Signed:
        // there is nothing left for them to do with one.
        isDraft || row.status === 'accepted' ? null : (
          <ResendLinkButton agreement={agreement} recipient={row} />
        ),
    },
  ];

  return (
    <Card>
      <CardHeader
        title="Signatories"
        description={
          recipients.length === 0
            ? // Said as a consequence, not as an empty table. Everything downstream is
              // keyed on recipients -- sending needs somebody to send to, execution is
              // recorded against a named one, and countersigning waits for all of them
              // -- so a draft with none is stranded with nothing on screen saying why.
              'Nobody has been added yet. This agreement cannot be sent until somebody has to sign it.'
            : isDraft
              ? // The most important sentence on the screen for a draft, and the one whose
                // absence caused a lead to be added as a signatory and never written to.
                'Nobody here has been sent anything. Each of them gets their own link when the agreement is issued.'
              : awaiting.length > 0
                ? `Each has their own link. Not accepted until every required signatory has acted — ${awaiting.length} outstanding.`
                : 'An agreement is not accepted until every required signatory has acted.'
        }
        actions={
          // Draft only. Recipients are fixed on send -- adding one afterwards would
          // bind somebody to a document they were not party to when it was issued.
          isDraft ? (
            <Can actions={agreement._actions} action="edit">
              <Button variant="secondary" size="sm" onClick={() => setAdding(true)}>
                Add signatory
              </Button>
            </Can>
          ) : undefined
        }
      />
      <AddRecipientDialog
        agreement={agreement}
        open={adding}
        onClose={() => setAdding(false)}
      />

      <Table
        caption="Signatories on this agreement"
        columns={columns}
        rows={recipients}
        rowKey={(row) => row.id}
        emptyTitle="No signatories"
        emptyDescription="Nobody has been named on this agreement. Recipients are fixed once it is issued."
        className="rounded-none border-0 shadow-none"
      />
    </Card>
  );
}

// ---------------------------------------------------------------------------
// The execution record
// ---------------------------------------------------------------------------

function ExecutionRecord({
  agreement,
  status,
}: {
  agreement: Agreement;
  status: AgreementStatusReport | undefined;
}) {
  return (
    <Card>
      <CardHeader title="Execution record" />
      <CardBody>
        <dl className="flex flex-col gap-3">
          <Row label="Drafted">{formatDateTime(agreement.created_at)}</Row>
          <Row label="Issued">
            {agreement.sent_at ? formatDateTime(agreement.sent_at) : 'Not yet issued'}
          </Row>
          <Row label="First viewed">
            {agreement.viewed_at ? (
              formatDateTime(agreement.viewed_at)
            ) : (
              <span className="text-ink-subtle">Not recorded</span>
            )}
          </Row>
          <Row label="Accepted">
            {agreement.accepted_at ? (
              formatDateTime(agreement.accepted_at)
            ) : (
              <span className="text-ink-subtle">—</span>
            )}
          </Row>
          <Row label="Signed">
            {agreement.signed_at ? (
              formatDateTime(agreement.signed_at)
            ) : (
              <span className="text-ink-subtle">—</span>
            )}
          </Row>
          <Row label="Expires">
            {agreement.expires_at ? (
              formatDateTime(agreement.expires_at)
            ) : (
              <span className="text-ink-subtle">No expiry</span>
            )}
          </Row>
          <Row label="Fully signed">
            {status ? (
              <Badge tone={status.is_fully_signed ? 'success' : 'warning'} size="sm">
                {status.is_fully_signed ? 'Yes' : 'Not yet'}
              </Badge>
            ) : (
              <span className="text-ink-subtle">—</span>
            )}
          </Row>
        </dl>

        {status?.document_checksum && (
          <div className="mt-4 border-t pt-3">
            <p className="text-xs font-medium uppercase tracking-wide text-ink-subtle">
              Document checksum
            </p>
            {/* Shown in full and selectable. An elided checksum cannot be compared,
                and comparison is the only thing a checksum is for. */}
            <p className="mt-1 break-all font-mono text-xs text-ink">
              {status.document_checksum}
            </p>
            <p className="mt-1 text-xs text-ink-muted">
              SHA-256 of the exact text above. A later dispute is settled by comparing
              this, not by asserting what the document said.
            </p>
          </div>
        )}
      </CardBody>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Signature evidence
// ---------------------------------------------------------------------------

function EvidencePanel({
  evidence,
  loading,
  error,
  onRetry,
  recipients,
}: {
  evidence: SignatureEvidence[] | undefined;
  loading: boolean;
  error: unknown;
  onRetry: () => void;
  recipients: AgreementRecipient[];
}) {
  const nameOf = (recipientId: string) =>
    recipients.find((recipient) => recipient.id === recipientId)?.display_name ??
    'Unknown signatory';

  return (
    <Card>
      <CardHeader
        title="Signature evidence"
        description="Append-only. Who accepted what, when, and that it has not changed since."
      />
      <CardBody>
        {loading && <Skeleton className="h-24 w-full" />}
        {!loading && Boolean(error) && <ErrorState error={error} onRetry={onRetry} size="sm" />}
        {!loading && !error && (evidence?.length ?? 0) === 0 && (
          <p className="text-sm text-ink-subtle">
            Nothing has been signed yet, so there is no evidence to show.
          </p>
        )}
        {!loading && !error && evidence && evidence.length > 0 && (
          <ul className="flex flex-col gap-3">
            {evidence.map((entry) => (
              <li key={entry.id} className="rounded border bg-surface-sunken px-3 py-2">
                <p className="font-medium text-ink">{nameOf(entry.recipient)}</p>
                <p className="text-sm text-ink-muted">
                  {humanise(entry.method)} · {formatDateTime(entry.signed_at)}
                </p>
                <dl className="mt-2 flex flex-col gap-1 text-xs text-ink-muted">
                  <div className="flex gap-2">
                    <dt className="shrink-0 font-medium">Checksum</dt>
                    <dd className="break-all font-mono">{entry.document_checksum}</dd>
                  </div>
                  <div className="flex gap-2">
                    <dt className="shrink-0 font-medium">From</dt>
                    <dd>{entry.ip_address ?? 'Address not recorded'}</dd>
                  </div>
                  {entry.provider_reference && (
                    <div className="flex gap-2">
                      <dt className="shrink-0 font-medium">Provider</dt>
                      <dd className="break-all">{entry.provider_reference}</dd>
                    </div>
                  )}
                  {entry.user_agent && (
                    <div className="flex gap-2">
                      <dt className="shrink-0 font-medium">Device</dt>
                      <dd className="break-all">{entry.user_agent}</dd>
                    </div>
                  )}
                </dl>
              </li>
            ))}
          </ul>
        )}
      </CardBody>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Counterparty
// ---------------------------------------------------------------------------

/**
 * Exactly one of `client` and `lead` is set (CR-01), so exactly one of these queries
 * is ever enabled. Both hooks are declared unconditionally, which is what keeps the
 * hook order stable.
 *
 * A failure here is not an error state for the page: not knowing the counterparty's
 * name does not stop anyone reading the agreement. It falls back to the link.
 */
function Counterparty({ agreement }: { agreement: Agreement }) {
  const client = useApiQuery<Client>(
    qk.detail('clients', agreement.client ?? ''),
    STAFF.client(agreement.client ?? ''),
    { enabled: Boolean(agreement.client), retry: false },
  );

  const lead = useApiQuery<Lead>(
    qk.detail('leads', agreement.lead ?? ''),
    STAFF.lead(agreement.lead ?? ''),
    { enabled: Boolean(agreement.lead), retry: false },
  );

  if (agreement.client) {
    return (
      <Link to={`/clients/${agreement.client}`} className="text-sm text-primary hover:underline">
        <Badge tone="neutral" size="sm" className="mr-1.5">
          Client
        </Badge>
        {client.data?.display_name ?? 'Open record'}
      </Link>
    );
  }

  if (agreement.lead) {
    return (
      <Link to={`/leads/${agreement.lead}`} className="text-sm text-primary hover:underline">
        {/* Pre-conversion. Worth saying, because it changes what the firm may assume. */}
        <Badge tone="info" size="sm" className="mr-1.5">
          Lead
        </Badge>
        {lead.data?.full_name ?? 'Open record'}
      </Link>
    );
  }

  return null;
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-xs font-medium uppercase tracking-wide text-ink-subtle">{label}</dt>
      <dd className="text-right text-base text-ink">{children}</dd>
    </div>
  );
}
